// ═══════════════════════════════════════════════════════════════════════════════
// Task Completion Page (Helper)
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { IsoDatePicker } from '../../components/IsoDatePicker';
import { NativeTimeInput } from '../../components/NativeTimeInput';
import { PhotoUploader, GpsToggle, OsmEmbedInteractiveMap } from '../../components/task';
import { AppButton } from '../../components/ui/AppButton';
import { useToast } from '../../components/ui/ToastProvider';
import { geocodingApi, taskInstanceApi } from '../../lib/api';
import { isLimitReachedError } from '../../lib/limitError';
import { navigateBack } from '../../lib/navigateBack';
import { uploadTaskPhotoViaBackend } from '../../lib/directPhotoUpload';
import { useOfflineSync } from '../../contexts/OfflineSyncContext';
import { readFileAsDataUrl } from '../../offline/dataUrl';
import { enqueueTaskSubmit } from '../../offline/offlineQueue';
import {
  getTodayDate,
  LEVEL1_DEFAULTS,
  TASK_KIND,
  USER_ROLE,
  COMMENT_TARGET_TYPE,
  type TaskInstanceWithSubmission,
} from '@fdh/shared';
import {
  CommentThread,
  UnsentCommentOnSubmitDialog,
  type CommentThreadHandle,
} from '../../components/comments';
import { isValidGpsCoordinatePair } from '../../lib/osmStaticMap';
import { getHelperTodayYmd, resolveDisplayTimeZone } from '../../lib/userTodayYmd';
import { isWithinHelperEarlySubmitRange } from '../../utils/taskEarlySubmit';
import { getFutureDueBannerMessage } from '../../utils/taskRelativeDueBanner';
import { TaskSubmitConfirmModal } from '../../components/task';
import {
  AutoTranslationHint,
  resolveTaskDisplayDescription,
  resolveTaskDisplayTitle,
  resolveTranslationTargetLanguageLabel,
  taskInstanceHasHelperTranslations,
} from '../../components/translation';

const getNowDatetimeInTimezone = (timezone: string): string => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date());
  const year = parts.find((part) => part.type === 'year')?.value ?? '';
  const month = parts.find((part) => part.type === 'month')?.value ?? '';
  const day = parts.find((part) => part.type === 'day')?.value ?? '';
  const hour = parts.find((part) => part.type === 'hour')?.value ?? '';
  const minute = parts.find((part) => part.type === 'minute')?.value ?? '';
  return `${year}-${month}-${day} ${hour}:${minute}`;
};

const splitDatetime = (value: string): { date: string; time: string } => {
  const [datePart = '', timePart = ''] = value.split(' ');
  return { date: datePart, time: timePart };
};

export const TaskCompletionPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const { showSuccessToast } = useToast();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { refreshPendingCount } = useOfflineSync();

  const [taskInstance, setTaskInstance] = useState<TaskInstanceWithSubmission | null>(null);
  const [photos, setPhotos] = useState<File[]>([]);
  const [gpsEnabled, setGpsEnabled] = useState(false);
  const [gpsLat, setGpsLat] = useState<number | ''>('');
  const [gpsLng, setGpsLng] = useState<number | ''>('');
  const [completedDate, setCompletedDate] = useState('');
  const [completedTime, setCompletedTime] = useState('');
  const [gpsAddress, setGpsAddress] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unsentCommentModalOpen, setUnsentCommentModalOpen] = useState(false);
  const [unsentCommentDialogBusy, setUnsentCommentDialogBusy] = useState(false);
  const [submitConfirmModalOpen, setSubmitConfirmModalOpen] = useState(false);
  const [showTaskContentOriginal, setShowTaskContentOriginal] = useState(false);
  const commentThreadRef = useRef<CommentThreadHandle | null>(null);

  useEffect(() => {
    setShowTaskContentOriginal(false);
  }, [language]);

  const fetchTaskInstance = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await taskInstanceApi.get(id, language);
      if (response.success && response.data) {
        setTaskInstance(response.data);
      } else {
        setError(response.error ?? t('task.review.loadError'));
      }
    } catch {
      setError(t('task.review.loadError'));
    } finally {
      setLoading(false);
    }
  }, [id, language, t]);

  useEffect(() => {
    fetchTaskInstance();
  }, [fetchTaskInstance]);

  useEffect(() => {
    if (!id || !taskInstance || !user) return;
    if (taskInstance.task.taskKind === TASK_KIND.INVENTORY) {
      navigate(`/tasks/inventory/${id}`, { replace: true });
      return;
    }
    const todayYmd = getHelperTodayYmd(user.settings?.timezone);
    if (
      taskInstance.dueDate > todayYmd &&
      !isWithinHelperEarlySubmitRange(taskInstance.dueDate, todayYmd)
    ) {
      navigate(`/task-instances/${id}`, { replace: true });
    }
  }, [id, taskInstance, user, navigate]);

  useEffect(() => {
    const timezone = resolveDisplayTimeZone(user?.settings.timezone);
    const now = splitDatetime(getNowDatetimeInTimezone(timezone));
    setCompletedDate(now.date);
    setCompletedTime(now.time);
  }, [user?.settings.timezone]);

  const handleGpsToggle = (enabled: boolean, lat: number | '', lng: number | '') => {
    setGpsEnabled(enabled);
    setGpsLat(lat);
    setGpsLng(lng);
  };

  const gpsMapCoords = useMemo(() => {
    if (!gpsEnabled || gpsLat === '' || gpsLng === '') {
      return null;
    }
    if (!isValidGpsCoordinatePair(gpsLat, gpsLng)) {
      return null;
    }
    return { lat: gpsLat, lng: gpsLng };
  }, [gpsEnabled, gpsLat, gpsLng]);

  useEffect(() => {
    const lat = typeof gpsLat === 'number' ? gpsLat : null;
    const lng = typeof gpsLng === 'number' ? gpsLng : null;
    if (!gpsEnabled || lat === null || lng === null) {
      setGpsAddress('');
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const response = await geocodingApi.reverse(lat, lng, language);
        if (cancelled) return;
        if (response.success && response.data?.displayName) {
          setGpsAddress(response.data.displayName);
          return;
        }
        setGpsAddress('');
      } catch {
        if (!cancelled) {
          setGpsAddress('');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [gpsEnabled, gpsLat, gpsLng, language]);

  const uploadPhotosToGcs = async (files: File[]): Promise<string[]> => {
    const urls: string[] = [];
    for (const file of files) {
      try {
        const url = await uploadTaskPhotoViaBackend(file, file.name || 'photo.jpg');
        urls.push(url);
      } catch {
        throw new Error('PHOTO_UPLOAD_FAILED');
      }
    }
    return urls;
  };

  const performTaskSubmit = useCallback(async () => {
    if (!id) {
      return;
    }

    const requirePhoto = Boolean(taskInstance?.task.requirePhoto);
    if (requirePhoto && photos.length < LEVEL1_DEFAULTS.MIN_TASK_PHOTOS) {
      setError(t('task.complete.photoRequired'));
      return;
    }

    if (!completedDate || !completedTime) {
      setError(t('task.complete.completedAtRequired'));
      return;
    }
    const completedAt = `${completedDate} ${completedTime}`;

    setSubmitting(true);
    setError(null);

    try {
      if (!navigator.onLine) {
        const photoUrls = await Promise.all(photos.map((f) => readFileAsDataUrl(f)));
        await enqueueTaskSubmit({
          taskInstanceId: id,
          payload: {
            photoUrls,
            gpsLat,
            gpsLng,
            gpsEnabled,
            note: '',
            completedAt,
          },
        });
        await refreshPendingCount();
        showSuccessToast(t('toast.offlineQueued'));
        navigate('/today', { replace: true });
        return;
      }

      const photoUrls = await uploadPhotosToGcs(photos);

      const response = await taskInstanceApi.submit(id, {
        photoUrls,
        gpsLat,
        gpsLng,
        gpsEnabled,
        note: '',
        completedAt,
      });

      if (response.success) {
        if (response.data) {
          setTaskInstance(response.data);
        }
        showSuccessToast(t('toast.task.completeSuccess'));
        navigate('/today', { replace: true });
      } else {
        setError(response.error ?? t('task.complete.submitError'));
      }
    } catch (caught) {
      if (isLimitReachedError(caught)) {
        return;
      }
      setError(
        caught instanceof Error && caught.message === 'PHOTO_UPLOAD_FAILED'
          ? t('task.complete.photoUploadError')
          : t('task.complete.submitError')
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    id,
    photos,
    gpsEnabled,
    gpsLat,
    gpsLng,
    completedDate,
    completedTime,
    navigate,
    t,
    refreshPendingCount,
    showSuccessToast,
    taskInstance?.task.requirePhoto,
  ]);

  const handleFormSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      if (!id) {
        return;
      }

      const requirePhoto = Boolean(taskInstance?.task.requirePhoto);
      if (requirePhoto && photos.length < LEVEL1_DEFAULTS.MIN_TASK_PHOTOS) {
        setError(t('task.complete.photoRequired'));
        return;
      }

      if (!completedDate || !completedTime) {
        setError(t('task.complete.completedAtRequired'));
        return;
      }

      if (commentThreadRef.current?.hasUnsentDraft()) {
        setUnsentCommentModalOpen(true);
        return;
      }

      const todayYmd = getHelperTodayYmd(user?.settings?.timezone);
      if (
        taskInstance &&
        taskInstance.dueDate > todayYmd &&
        isWithinHelperEarlySubmitRange(taskInstance.dueDate, todayYmd)
      ) {
        setSubmitConfirmModalOpen(true);
        return;
      }

      void performTaskSubmit();
    },
    [
      id,
      photos.length,
      completedDate,
      completedTime,
      performTaskSubmit,
      taskInstance,
      user?.settings?.timezone,
      t,
    ],
  );

  const handleCloseSubmitConfirmModal = () => {
    if (submitting) {
      return;
    }
    setSubmitConfirmModalOpen(false);
  };

  const handleConfirmSubmitModal = () => {
    setSubmitConfirmModalOpen(false);
    void performTaskSubmit();
  };

  const handleUnsentCommentBackToEdit = useCallback(() => {
    if (unsentCommentDialogBusy) {
      return;
    }
    setUnsentCommentModalOpen(false);
    commentThreadRef.current?.openComposer();
  }, [unsentCommentDialogBusy]);

  const handleUnsentCommentSubmitOnly = useCallback(() => {
    if (unsentCommentDialogBusy) {
      return;
    }
    commentThreadRef.current?.clearDraft();
    setUnsentCommentModalOpen(false);
    void performTaskSubmit();
  }, [unsentCommentDialogBusy, performTaskSubmit]);

  const handleUnsentCommentSubmitWithComment = useCallback(async () => {
    if (unsentCommentDialogBusy) {
      return;
    }
    setUnsentCommentDialogBusy(true);
    const sent = await commentThreadRef.current?.sendDraft();
    setUnsentCommentDialogBusy(false);
    if (sent === false) {
      return;
    }
    setUnsentCommentModalOpen(false);
    void performTaskSubmit();
  }, [unsentCommentDialogBusy, performTaskSubmit]);

  const handleBack = () => {
    navigateBack(navigate, '/today');
  };

  const helperTodayYmd = useMemo(
    () => getHelperTodayYmd(user?.settings?.timezone),
    [user?.settings?.timezone],
  );

  const futureDueBannerMessage = useMemo(() => {
    if (!taskInstance) {
      return null;
    }
    return getFutureDueBannerMessage(
      USER_ROLE.HELPER,
      helperTodayYmd,
      taskInstance.dueDate,
      t,
    );
  }, [helperTodayYmd, taskInstance, t]);

  if (loading) {
    return (
      <div className="flex min-h-full items-center justify-center bg-gray-50">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
      </div>
    );
  }

  if (error && !taskInstance) {
    return (
      <div className="flex min-h-full flex-col items-center justify-center bg-gray-50 px-4">
        <p className="text-center text-red-600" role="alert">{error}</p>
        <button
          type="button"
          onClick={handleBack}
          className="mt-4 text-primary-600 underline hover:no-underline focus:outline-none"
        >
          {t('task.action.back')}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-50">
      {/* Header */}
      <header className="z-10 bg-white shadow-sm">
        <div className="mx-auto max-w-lg px-4 py-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleBack}
              className="flex h-10 w-10 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label={t('task.action.back')}
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="flex min-w-0 flex-1 items-start justify-between gap-2">
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-gray-900">{t('task.complete.title')}</h1>
                <p className="text-sm text-gray-500">{t('task.complete.subtitle')}</p>
              </div>
              {taskInstance && taskInstanceHasHelperTranslations(taskInstance) ? (
                <AutoTranslationHint
                  targetLanguageLabel={resolveTranslationTargetLanguageLabel(
                    t,
                    taskInstance.task.translation?.targetLanguage,
                    language,
                  )}
                  showingOriginal={showTaskContentOriginal}
                  onToggleOriginal={() => setShowTaskContentOriginal((prev) => !prev)}
                />
              ) : null}
            </div>
          </div>
        </div>
      </header>

      {futureDueBannerMessage ? (
        <div className="mx-auto max-w-lg px-4 pt-4">
          <div
            className="flex items-center gap-2 rounded-xl bg-yellow-50 px-4 py-3 text-yellow-700 dark:bg-yellow-950/40 dark:text-yellow-200"
            role="status"
            aria-live="polite"
            aria-label={futureDueBannerMessage}
          >
            <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <span className="text-sm font-medium">{futureDueBannerMessage}</span>
          </div>
        </div>
      ) : null}

      {/* Task Info */}
      {taskInstance && (
        <div className={`mx-auto max-w-lg px-4 ${futureDueBannerMessage ? 'pt-3' : 'pt-4'}`}>
          <div className="rounded-xl bg-white p-4 shadow-sm dark:bg-gray-900 dark:shadow-black/20">
            <h2 className="font-semibold text-gray-900 dark:text-gray-100">
              {resolveTaskDisplayTitle(taskInstance.task, showTaskContentOriginal)}
            </h2>
            {taskInstance.task.description ? (
              <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-600 dark:text-gray-300">
                {resolveTaskDisplayDescription(taskInstance.task, showTaskContentOriginal)}
              </p>
            ) : null}
            <div className="mt-4 space-y-2 border-t border-gray-200 pt-4 text-sm text-gray-600 dark:border-gray-700 dark:text-gray-300">
              <div className="flex justify-between">
                <span>{t('task.review.taskDate')}</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{taskInstance.dueDate}</span>
              </div>
              {taskInstance.task.dueTime ? (
                <div className="flex justify-between">
                  <span>{t('task.form.dueTimeLabel')}</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{taskInstance.task.dueTime}</span>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <form
        id="task-complete-form"
        onSubmit={handleFormSubmit}
        className="mx-auto max-w-lg space-y-6 px-4 py-6 pb-8"
      >
        {/* Photo upload only when task requires photos */}
        {taskInstance?.task.requirePhoto ? (
          <div className="rounded-xl bg-white p-4 shadow-sm">
            <PhotoUploader
              photos={photos}
              onPhotosChange={setPhotos}
              maxPhotos={LEVEL1_DEFAULTS.MAX_TASK_PHOTOS}
              disabled={submitting}
            />
          </div>
        ) : null}

        <div className="rounded-xl bg-white p-4 shadow-sm">
          <label className="block text-sm font-medium text-gray-700">
            {t('task.complete.completedAtLabel')}
          </label>
          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <IsoDatePicker
              id="completedDate"
              ariaLabel={t('task.complete.completedAtLabel')}
              value={completedDate}
              onChange={setCompletedDate}
              maxDate={getTodayDate()}
              disabled={submitting}
              valueFormat="iso"
            />
            <NativeTimeInput
              id="completedTime"
              aria-label={t('task.complete.completedAtLabel')}
              value={completedTime}
              onChange={(e) => setCompletedTime(e.target.value)}
              disabled={submitting}
              className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
        </div>

        {/* GPS Toggle + interactive OSM embed when coordinates are available */}
        <div className="rounded-xl bg-white p-4 shadow-sm">
          <GpsToggle enabled={gpsEnabled} onToggle={handleGpsToggle} disabled={submitting} />
          {gpsMapCoords ? (
            <div className="mt-4 border-t border-gray-100 pt-4">
              <h3 className="mb-3 text-sm font-medium text-gray-700">{t('task.review.gpsLocation')}</h3>
              <OsmEmbedInteractiveMap lat={gpsMapCoords.lat} lng={gpsMapCoords.lng}>
                <div>
                  <h4 className="text-xs font-medium text-gray-500">{t('task.review.addressLabel')}</h4>
                  {gpsAddress ? (
                    <p className="mt-1 text-sm leading-relaxed text-gray-800">{gpsAddress}</p>
                  ) : (
                    <p className="mt-1 text-sm text-gray-500">{t('task.review.addressUnavailable')}</p>
                  )}
                </div>
              </OsmEmbedInteractiveMap>
            </div>
          ) : null}
        </div>

        {id ? (
          <CommentThread
            ref={commentThreadRef}
            targetType={COMMENT_TARGET_TYPE.TASK_INSTANCE}
            targetId={id}
          />
        ) : null}

        {/* Error */}
        {error && (
          <div className="rounded-xl bg-red-50 p-4 text-center">
            <p className="text-sm text-red-600" role="alert">{error}</p>
          </div>
        )}

        <UnsentCommentOnSubmitDialog
          open={unsentCommentModalOpen}
          context="taskComplete"
          busy={unsentCommentDialogBusy}
          onBackToEdit={handleUnsentCommentBackToEdit}
          onSubmitOnly={handleUnsentCommentSubmitOnly}
          onSubmitWithComment={handleUnsentCommentSubmitWithComment}
        />

        <TaskSubmitConfirmModal
          open={submitConfirmModalOpen}
          dueDateYmd={taskInstance?.dueDate ?? ''}
          todayYmd={helperTodayYmd}
          t={t}
          title={t('task.submitConfirmTitle')}
          prompt={t('task.submitConfirmPrompt')}
          confirmLabel={t('task.submitConfirmAction')}
          cancelLabel={t('common.cancel')}
          submitting={submitting}
          onClose={handleCloseSubmitConfirmModal}
          onConfirm={handleConfirmSubmitModal}
        />

        <div className="border-t border-gray-100 pt-6">
          <AppButton
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            disabled={submitting || (taskInstance?.task.requirePhoto === true && photos.length < LEVEL1_DEFAULTS.MIN_TASK_PHOTOS)}
            className="shadow-md py-4"
          >
            {submitting ? t('task.complete.submitting') : t('task.complete.submit')}
          </AppButton>
        </div>
      </form>
    </div>
  );
};
